import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, InputBase, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import React, { useCallback, useId, useState } from 'react';

import { StructureFilterBarProps, StructureFilterState } from './StructureFilterBar.props';
import { useFilterDirty } from '../../../hooks/useFilterDirty';
import {
  ActiveFiltersBar,
  AddFilterModal,
  AdvancedFilter,
  getSearchButtonDirtySx,
  MultiSelectDropdown,
  SEARCH_BUTTON_PENDING_HINT,
  filterStripSx,
  searchButtonSx,
  secondaryButtonSx,
  textFieldSx,
} from '../FilterBar';

const EMPTY_STATE: StructureFilterState = {
  locationTypes: [],
  contracts: [],
  activeStatus: [],
  locationId: '',
  multiSearch: '',
  advancedFilters: [],
};

export const StructureFilterBar: React.FC<StructureFilterBarProps> = ({
  onSearch,
  locationTypes,
  locationTypeGroups,
  contracts,
  activeStatusOptions,
  fieldDefinitions,
  fieldCategories,
  initialFilters,
}) => {
  const theme = useTheme();
  const pendingHintId = useId();
  const [filters, setFilters] = useState<StructureFilterState>({
    ...EMPTY_STATE,
    ...initialFilters,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [recentlyUsed, setRecentlyUsed] = useState<string[]>([]);
  const { isDirty, markApplied } = useFilterDirty(filters);

  const updateFilter = useCallback(
    <K extends keyof StructureFilterState>(key: K, value: StructureFilterState[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const handleSearch = useCallback(() => {
    onSearch(filters);
    markApplied(filters);
  }, [filters, onSearch, markApplied]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSearch();
    },
    [handleSearch]
  );

  const handleAddFilter = useCallback((filter: AdvancedFilter) => {
    setFilters((prev) => ({
      ...prev,
      advancedFilters: [...prev.advancedFilters, filter],
    }));
    setRecentlyUsed((prev) =>
      prev.includes(filter.dataKey) ? prev : [filter.dataKey, ...prev].slice(0, 5)
    );
  }, []);

  // Re-run the search on removal so the deleted filter clears from the results
  // immediately, without waiting for a manual Search or a page refresh.
  const handleRemoveFilter = useCallback(
    (index: number) => {
      const next = {
        ...filters,
        advancedFilters: filters.advancedFilters.filter((_, i) => i !== index),
      };
      setFilters(next);
      onSearch(next);
      markApplied(next);
    },
    [filters, onSearch, markApplied]
  );

  // Inline chip edit: replace the filter in place and re-run the search
  // immediately, mirroring the remove-chip behaviour above (and the Jobs/
  // Parts FilterBar, which already supports this).
  const handleEditFilter = useCallback(
    (index: number, filter: AdvancedFilter) => {
      if (index >= filters.advancedFilters.length) return;
      const next = {
        ...filters,
        advancedFilters: filters.advancedFilters.map((f, i) => (i === index ? filter : f)),
      };
      setFilters(next);
      onSearch(next);
      markApplied(next);
    },
    [filters, onSearch, markApplied]
  );

  const handleClearAll = useCallback(() => {
    const next = { ...filters, advancedFilters: [] };
    setFilters(next);
    onSearch(next);
    markApplied(next);
  }, [filters, onSearch, markApplied]);

  const inputSx = textFieldSx(theme, false);

  return (
    <Box>
      <Box sx={filterStripSx(theme, filters.advancedFilters.length > 0)} onKeyDown={handleKeyDown}>
        <MultiSelectDropdown
          label="Location Type"
          options={locationTypes}
          selected={filters.locationTypes}
          onChange={(val) => updateFilter('locationTypes', val)}
          groups={locationTypeGroups}
        />
        <MultiSelectDropdown
          label="Contract"
          options={contracts}
          selected={filters.contracts}
          onChange={(val) => updateFilter('contracts', val)}
        />
        <MultiSelectDropdown
          label="Active"
          options={activeStatusOptions}
          selected={filters.activeStatus}
          onChange={(val) => updateFilter('activeStatus', val)}
        />

        <InputBase
          placeholder="Location ID"
          value={filters.locationId}
          onChange={(e) => updateFilter('locationId', e.target.value)}
          inputProps={{ 'aria-label': 'Location ID filter' }}
          sx={{ ...inputSx, width: 120 }}
        />

        <Button
          startIcon={<AddIcon sx={{ fontSize: 15 }} />}
          onClick={() => setModalOpen(true)}
          aria-label="Add advanced filter"
          sx={secondaryButtonSx(theme)}
        >
          Add Filter
        </Button>

        <InputBase
          placeholder="Search name or reference..."
          value={filters.multiSearch}
          onChange={(e) => updateFilter('multiSearch', e.target.value)}
          inputProps={{ 'aria-label': 'Search by name or reference number' }}
          sx={{ ...inputSx, width: 240, ml: 'auto' }}
        />
        <Button
          variant="contained"
          size="small"
          disableElevation
          startIcon={<SearchIcon sx={{ fontSize: 15 }} />}
          onClick={handleSearch}
          aria-label="Search structures"
          aria-describedby={isDirty ? pendingHintId : undefined}
          sx={{
            ...searchButtonSx(theme),
            ...getSearchButtonDirtySx(theme, isDirty),
          }}
        >
          Search
        </Button>
        <Box id={pendingHintId} sx={visuallyHidden}>
          {SEARCH_BUTTON_PENDING_HINT}
        </Box>
      </Box>

      <ActiveFiltersBar
        filters={filters.advancedFilters}
        onRemove={handleRemoveFilter}
        onClearAll={handleClearAll}
        onEdit={handleEditFilter}
        fieldDefinitions={fieldDefinitions}
      />

      <AddFilterModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onApply={handleAddFilter}
        recentlyUsed={recentlyUsed}
        fieldDefinitions={fieldDefinitions}
        categories={fieldCategories}
      />
    </Box>
  );
};
